/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import _ from 'lodash'

import { Container, Flex } from '@stackhouse/styled-elements'

import CollapseSection from '@stackhouse/dashboard-components/CollapseSection'
import { createFormInstance } from '@stackhouse/dashboard-components/Form'

import InputDate from '@stackhouse/dashboard-components/Input/Date'
import InputDateRange from '@stackhouse/dashboard-components/Input/DateRange'
import isMobile from '../../utils/is-mobile'


const Form = createFormInstance()

const dateContainerProps = { flex: 1, minWidth: 0 }
const dateRangeContainerProps = { width: `calc(50% - 5px)` }


const SectionComponent = React.memo(({ children, title, open, hidden }) => {

    const [isOpen, setIsOpen] = useState(open)
    useEffect(() => setIsOpen(open), [open])

    const content = (
        <Flex column pv={10} ph={5}>
            {children}
        </Flex>
    )

    if (hidden) {
        return (
            <Container hidden>
                {content}
            </Container>
        )
    }

    if (!title || title === 'undefined') {
        return content
    }

    return (
        <CollapseSection key={title} title={title} open={isOpen} onToggle={setIsOpen}>
            {content}
        </CollapseSection>
    )
})

const GroupComponent = React.memo(({ children }) => {
    return (
        <Flex row wrap minWidth={0}>
            {children}
        </Flex>
    )
})

const SpacerComponent = React.memo(({ children, ...props }) => {
    return <Container {...props} />
})


const DateFlexComponent = React.memo(({ containerProps, ...props }) => {
    return (
        <Flex {...containerProps} minWidth={0}>
            <InputDate {...props} containerProps={dateContainerProps} />
        </Flex>
    )
})


const DateRangeFlexComponent = React.memo(({ containerProps, ...props }) => {
    return (
        <Flex {...containerProps} width={250} minWidth={0} justifyBetween>
            <InputDateRange {...props} containerProps={dateRangeContainerProps} />
        </Flex>
    )
})

DateFlexComponent.validate = InputDate.validate
DateRangeFlexComponent.validate = InputDateRange.validate


Form.extendControls({
    section: SectionComponent,
    group: GroupComponent,
    spacer: SpacerComponent,
    date: DateFlexComponent,
    daterange: DateRangeFlexComponent,
})


const useMemoFields = (fields, openSections, hiddenSections) => {
    return useMemo(() => {
        const bySection = _.groupBy(fields, 'section')
        const fieldsSections = Object.entries(bySection).reduce((acc, [section, fields]) => {
            return [...acc, {
                fields,
                type: 'section',
                title: section,
                open: openSections ? openSections.includes(section) : null,
                hidden: hiddenSections ? hiddenSections.includes(section) : null,
            }]
        }, [])

        const fieldsSectionsGroups = fieldsSections.map(({ fields, ...section }) => {
            const byGroup = _.groupBy(fields, 'group')
            const fieldsGroups = Object.entries(byGroup).reduce((acc, [group, fields]) => {
                const flexFields = fields.reduce((acc, inc, index, array) => {
                    if (!inc) return acc

                    const extraProps = {}
                    const { fr = 1, ...field } = inc

                    if (isMobile && inc.hidden) {
                        extraProps.display = 'none'
                    }

                    return [...acc, {
                        ...extraProps,
                        type: 'flex',
                        flex: `${fr} 0 0`,
                        fields: [{ ...field, containerProps: { flex: 1, paddingHorizontal: 5, minWidth: 0 } }]
                    }]
                }, [])

                if (fields.filter(Boolean).every(field => field.hidden)) {
                    return acc
                }

                return [...acc, { type: 'group', fields: flexFields }]
            }, [])

            return { ...section, fields: fieldsGroups }
        })

        return { fields: fieldsSectionsGroups, key: new Date().getTime().toString() }
    }, [fields, openSections, hiddenSections])
}

export const ResponsiveForm = React.memo(React.forwardRef(({ fields, openSections, hiddenSections, ...props }, ref) => {
    const { key, fields: memoFields } = useMemoFields(fields, openSections, hiddenSections)
    return <Form key={key} ref={ref} fields={memoFields} {...props} />
}))


ResponsiveForm.propTypes = {
    fields: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
    openSections: PropTypes.array,
    hiddenSections: PropTypes.array,
}

export default ResponsiveForm